import * as THREE from 'three';
import type { NodeImplementation } from '@spotar/runtime';
import { applyEaseCurve } from '../helper/easing-helper';
import { runFrameAnimation, stopFrameAnimation } from '../helper/frame-animation-helper';
import { sceneHelper } from '../helper/scene-helper';
import type { Object3D } from 'three';

const CM_TO_M = 0.01;
const POSITION_EPSILON = 1e-6;

type MoveToData = {
  target: { id: string };
  position: THREE.Vector3;
  speed?: number;
  curve?: string;
};

function getMoveAnimationKey(object: Object3D): string {
  return `position:${object.uuid}`;
}

function animateObjectPosition(
  object: Object3D,
  targetMeters: THREE.Vector3,
  speedMetersPerSecond: number,
  curve: string | undefined
): Promise<boolean> {
  const animationKey = getMoveAnimationKey(object);
  const start = object.position.clone();
  const distance = start.distanceTo(targetMeters);

  if (distance < POSITION_EPSILON) {
    stopFrameAnimation(animationKey);
    object.position.copy(targetMeters);
    return Promise.resolve(true);
  }

  return runFrameAnimation({
    key: animationKey,
    durationSeconds: distance / speedMetersPerSecond,
    onProgress: (progress) =>
      object.position.lerpVectors(start, targetMeters, applyEaseCurve(progress, curve)),
    onComplete: () => object.position.copy(targetMeters)
  });
}

export const runMoveToImplementation: NodeImplementation = async (data, _nodeId) => {
  const { target, position, speed, curve } = data as MoveToData;
  const object = sceneHelper?.getObjectByUUID(target.id);
  if (!object) {
    console.error(`Object ${target.id} not found`);
    return;
  }

  const speedCmPerSecond = speed ?? 100;
  const speedMetersPerSecond = speedCmPerSecond * CM_TO_M;

  await animateObjectPosition(object, position, speedMetersPerSecond, curve);
};
